/**
 * @class Machine
 *
 * @property {Number}   id
 * @property {String}   name
 * @property {String}   serialNumber
 * @property {Number}   type
 * @property {String}   typeName
 * @property {Number}   channels
 * @property {Number}   canisters
 * @property {Boolean}  isSelected
 * @property {Boolean}  isConnected
 * @property {Boolean}  isBusy
 * @property {Boolean}  isDispensing
 * @property {Boolean}  dialog              If true, machine dialog is opened
 * @property {Object}   methods             Supported methods by connected machine
 * @property {Array}    autotaskTypes       Supported autotask types
 * @property {Object}   devices             Available machine devices
 * @property {Object}   sensors             Updated machine sensors
 * @property {Array}    errors              Machine errors
 * @property {Number}   productImageId
 * @property {Array}    historyActions      History of machine actions
 * @property {Object}   refillValidation
 */
export default class Machine {
    /**
     * @param {Number} id
     */
    constructor(id) {
        this._id = id;
        this._name = "";
        this._serialNumber = "C-3PO";
        this._type = null;
        this._typeName = "";
        this._channels = 0;
        this._canisters = 0;
        this._isSelected = false;
        this._isConnected = false;
        this._isBusy = false;
        this._isDispensing = false;
        this._dialog = null;
        this._methods = {};
        this._autotaskTypes = [];
        this._devices = {};
        this._sensors = null;
        this._productImageId = -1;
        this._errors = [];
        this._historyActions = [];
        this._refillValidation = null;
    }

    /**
     * @return {Number}
     */
    get id() {
        return this._id;
    }

    /**
     * @param {Number} id
     */
    set id(id) {
        this._id = id;
    }

    /**
     * @param {String} name
     */
    set name(name) {
        this._name = name;
    }

    /**
     * @return {String}
     */
    get name() {
        return this._name;
    }

    /**
     * @param {String} serialNumber
     */
    set serialNumber(serialNumber) {
        this._serialNumber = serialNumber;
    }

    /**
     * @return {String}
     */
    get serialNumber() {
        return this._serialNumber;
    }

    /**
     * @return {Number}
     */
    get type() {
        return this._type;
    }

    /**
     * @param {Number} type
     */
    set type(type) {
        this._type = type;
    }

    /**
     * @param {String} typeName
     */
    set typeName(typeName) {
        this._typeName = typeName;
    }

    /**
     * @return {String}
     */
    get typeName() {
        return this._typeName;
    }

    /**
     * @return {Number}
     */
    get channels() {
        return this._channels;
    }

    /**
     * @param {Number} channels
     */
    set channels(channels) {
        this._channels = channels;
    }

    /**
     * @return {Number}
     */
    get canisters() {
        return this._canisters;
    }

    /**
     * @param {Number} canisters
     */
    set canisters(canisters) {
        this._canisters = canisters;
    }

    /**
     * @param {Boolean} isBusy
     */
    set isBusy(isBusy) {
        this._isBusy = isBusy;
    }

    /**
     * @return {Boolean}
     */
    get isBusy() {
        return this._isBusy;
    }

    /**
     * @param {Boolean} isDispensing
     */
    set isDispensing(isDispensing) {
        this._isDispensing = isDispensing;
    }

    /**
     * @return {Boolean}
     */
    get isDispensing() {
        return this._isDispensing;
    }

    /**
     * @param {Boolean} isSelected
     */
    set isSelected(isSelected) {
        this._isSelected = isSelected;
    }

    /**
     * @return {Boolean}
     */
    get isSelected() {
        return this._isSelected;
    }

    /**
     * @param {Boolean} isConnected
     */
    set isConnected(isConnected) {
        this._isConnected = isConnected;
    }

    /**
     * @return {Boolean}
     */
    get isConnected() {
        return this._isConnected;
    }

    /**
     * @return {Object}
     */
    get dialog() {
        return this._dialog;
    }

    /**
     * @param {Object} dialog
     */
    set dialog(dialog) {
        this._dialog = dialog;
    }

    /**
     * @return {Object}
     */
    get methods() {
        return this._methods;
    }

    /**
     * @param {Object} methods
     */
    set methods(methods) {
        this._methods = methods;
    }

    /**
     * @return {Array}
     */
    get autotaskTypes() {
        return this._autotaskTypes;
    }

    /**
     * @param {Array} autotaskTypes
     */
    set autotaskTypes(autotaskTypes) {
        this._autotaskTypes = autotaskTypes;
    }

    /**
     * @return {Object}
     */
    get sensors() {
        return this._sensors;
    }

    /**
     * @param {Object} devices
     */
    set sensors(sensors) {
        this._sensors = sensors;
    }

    /**
     * @return {Object}
     */
    get devices() {
        return this._devices;
    }

    /**
     * @param {Object} devices
     */
    set devices(devices) {
        this._devices = devices;
    }

    /**
     * @return {Array}
     */
    get errors() {
        return this._errors;
    }

    /**
     * @param {Array} errors
     */
    set errors(errors) {
        this._errors = errors;
    }

    /**
     * @param   {Number}    productImageId
     */
    set productImageId(productImageId) {
        this._productImageId = productImageId;
    }

    /**
     * @return {Number}
     */
    get productImageId() {
        return this._productImageId;
    }

    /**
     * @return {Array}
     */
    get historyActions() {
        return this._historyActions;
    }

    /**
     * @param {Array} historyActions
     */
    set historyActions(historyActions) {
        this._historyActions = historyActions;
    }

    /**
     * @return {Object}
     */
    get refillValidation() {
        return this._refillValidation;
    }

    /**
     * @param {Object} refillValidation
     */
    set refillValidation(refillValidation) {
        this._refillValidation = refillValidation;
    }

    /**
     * @return {String}
     */
    toString() {
        return "Machine (" + this._id + ")";
    }

    /**
     * @param   {String}    method
     * @return  {Boolean}
     */
    validMethod = (method) => {
        return this._methods[method] === true;
    };

    /**
     * @param   {String}    device
     * @return  {Boolean}
     */
    validDevice = (device) => {
        return this._devices[device] === true;
    };

    /**
     * Resets machine to default state.
     *
     * @return  {Object}
     */
    reset = () => {
        let machine = new Machine(this._id);
        machine.name = "";
        machine.serialNumber = "C-3PO";
        machine.type = this._type;
        machine.typeName = this._typeName;
        machine.channels = 0;
        machine.canisters = 0;
        machine.isBusy = false;
        machine.isSelected = false;
        machine.isConnected = false;
        machine.dialog = null;
        machine.methods = {};
        machine.autotaskTypes = [];
        machine.devices = {};
        machine.errors = [];
        machine.productImageId = -1;
        machine.historyActions = [];
        machine.sensors = [];
        machine.refillValidation = null;

        return machine;
    };

    /**
     * Clones machine object.
     *
     * @return  {Object}
     */
    cloneMachine = () => {
        let machine = new Machine(this._id);
        machine.name = this._name;
        machine.serialNumber = this._serialNumber;
        machine.type = this._type;
        machine.typeName = this._typeName;
        machine.channels = this._channels;
        machine.canisters = this._canisters;
        machine.isBusy = this._isBusy;
        machine.isDispensing = this._isDispensing;
        machine.isSelected = this._isSelected;
        machine.isConnected = this._isConnected;
        machine.dialog = this._dialog;
        machine.methods = this._methods;
        machine.autotaskTypes = this._autotaskTypes;
        machine.devices = this._devices;
        machine.errors = this._errors;
        machine.productImageId = this._productImageId;
        machine.historyActions = this._historyActions;
        machine.sensors = this._sensors;
        machine.refillValidation = this._refillValidation;

        return machine;
    };

    /**
     * Clones machine object and updates methods property.
     *
     * @param   {Object}    methods
     * @return  {Object}
     */
    updateMethods = (methods) => {
        let machine = this.cloneMachine();
        machine.methods = methods;

        return machine;
    };

    /**
     * Clones machine object and updates autotask types property.
     *
     * @param   {Array} autotaskTypes
     * @return  {Object}
     */
    updateAutotaskTypes = (autotaskTypes) => {
        let machine = this.cloneMachine();
        machine.autotaskTypes = autotaskTypes;

        return machine;
    };

    /**
     * Clones machine object and updates machine info properties.
     *
     * @param   {String} name
     * @param   {String} serialNumber
     * @param   {Number} type
     * @param   {String} typeName
     * @param   {Number} channels
     * @param   {Number} canisters
     * @return  {Object}
     */
    updateInfo = (name, serialNumber, type, typeName, channels, canisters) => {
        let machine = this.cloneMachine();
        machine.name = name;
        machine.serialNumber = serialNumber;
        machine.type = type;
        machine.typeName = typeName;
        machine.channels = channels;
        machine.canisters = canisters;

        return machine;
    };

    /**
     * Clones machine object and updates machine typeName property.
     *
     * @param   {String} typeName
     * @return  {Object}
     */
    updateTypeName = (typeName) => {
        let machine = this.cloneMachine();
        machine.typeName = typeName;

        return machine;
    };

    /**
     * Clones machine object and updates devices property.
     *
     * @param   {Object} devices
     * @return  {Object}
     */
    updateDevices = (devices) => {
        let machine = this.cloneMachine();
        machine.devices = devices;

        return machine;
    };

    /**
     * Clones machine object and updates isBusy property.
     *
     * @param   {Boolean} isBusy
     * @return  {Object}
     */
    updateIsBusy = (isBusy) => {
        let machine = this.cloneMachine();
        machine.isBusy = isBusy;

        return machine;
    };

    /**
     * Clones machine object and updates isDispensing property.
     *
     * @param   {Boolean} isDispensing
     * @return  {Object}
     */
    updateIsDispensing = (isDispensing) => {
        let machine = this.cloneMachine();
        machine.isDispensing = isDispensing;

        return machine;
    };

    /**
     * Clones machine object and updates dialog property.
     *
     * @param   {Object} dialog
     * @return  {Object}
     */
    updateDialog = (dialog) => {
        let machine = this.cloneMachine();
        machine.dialog = dialog;

        return machine;
    };

    /**
     * Clones machine object and updates dialog and validation property.
     *
     * @param   {Object} dialog
     * @param   {Object} validation
     * @return  {Object}
     */
    updateDialogAndValidation = (dialog, validation) => {
        let machine = this.cloneMachine();
        machine.dialog = dialog;
        machine.refillValidation = validation;

        return machine;
    };

    /**
     * Clones machine object and updates validation canisterIndex property.
     *
     * @param   {Number} canisterIndex
     * @return  {Object}
     */
    updateValidationCanisterIndex = (canisterIndex) => {
        let machine = this.cloneMachine();

        if (this._refillValidation === null) {
            return machine;
        }

        machine.refillValidation = this._refillValidation.updateCanisterIndex(canisterIndex);

        return machine;
    };

    /**
     * Clones machine object and updates validation canister property.
     *
     * @param   {Object} canister
     * @return  {Object}
     */
    updateValidationCanister = (canister) => {
        let machine = this.cloneMachine();

        if (this._refillValidation === null) {
            return machine;
        }

        machine.refillValidation = this._refillValidation.updateCanister(canister);

        return machine;
    };

    /**
     * Clones machine object and updates validation colorantPackage property.
     *
     * @param   {Object} colorantPackage
     * @return  {Object}
     */
    updateValidationColorantPackage = (colorantPackage) => {
        let machine = this.cloneMachine();

        if (this._refillValidation === null) {
            return machine;
        }

        machine.refillValidation = this._refillValidation.updateColorantPackage(colorantPackage);

        return machine;
    };

    /**
     * Clones machine object and resets validation properties.
     *
     * @return  {Object}
     */
    resetValidation = () => {
        let machine = this.cloneMachine();

        if (this._refillValidation === null) {
            return machine;
        }

        machine.refillValidation = this._refillValidation.reset();

        return machine;
    };

    /**
     * Clones machine object and resets colorant package validation properties.
     *
     * @return  {Object}
     */
    resetColorantPackageValidation = () => {
        let machine = this.cloneMachine();

        if (this._refillValidation === null) {
            return machine;
        }

        machine.refillValidation = this._refillValidation.resetColorantPackage();

        return machine;
    };

    /**
     * Clones machine object and updates dialog property.
     *
     * @param   {Object} dialog
     * @return  {Object}
     */
    updateSensors = (sensors) => {
        let machine = this.cloneMachine();
        machine.sensors = sensors;

        return machine;
    };

    /**
     * Clones machine object and updates progress in dialog property.
     *
     * @param   {Number | null} percentage
     * @param   {Number | null} timeLeft
     * @param   {Array}         progressItems
     * @return  {Object}
     */
    updateDialogProgress = (percentage, timeLeft, progressItems, progressItemsCount, progressItemsDone) => {
        let machine = this.cloneMachine();
        machine.dialog = this._dialog.updateProgressItems(
            percentage,
            timeLeft,
            progressItems,
            progressItemsCount,
            progressItemsDone
        );

        return machine;
    };

    /**
     * Clones machine object and updates errors.
     *
     * @param   {Array} errors
     * @return  {Object}
     */
    updateErrors = (errors) => {
        let machine = this.cloneMachine();

        if (errors === null || errors.length === 0) {
            machine.errors = [];

            return machine;
        }

        machine.errors = errors;

        return machine;
    };

    /**
     * Clones machine object and updates product image ID.
     *
     * @param   {Number} productImageId
     * @return  {Object}
     */
    updateProductImageId = (productImageId) => {
        let machine = this.cloneMachine();
        machine.productImageId = productImageId;

        return machine;
    };

    /**
     * Clones machine object and updates history actions.
     *
     * @param   {Array} historyActions
     * @return  {Object}
     */
    updateHistoryActions = (historyActions) => {
        let machine = this.cloneMachine();
        machine.historyActions = historyActions;

        return machine;
    };

    /**
     * Clones machine object and updates isSelected value.
     *
     * @param   {Boolean}   isSelected
     * @return  {Object}
     */
    updateIsSelected = (isSelected) => {
        let machine = this.cloneMachine();
        machine.isSelected = isSelected;

        return machine;
    };

    /**
     * Clones machine object and updates isConnected value.
     *
     * @param   {Boolean}   isConnected
     * @return  {Object}
     */
    updateIsConnected = (isConnected) => {
        let machine = this.cloneMachine();
        machine.isConnected = isConnected;

        return machine;
    };

    /**
     * Clones machine object and updates progress in dialog property.
     *
     * @param   {String}    text
     * @return  {Object}
     */
    updateDialogText = (text) => {
        if (this._dialog === null) {
            return this;
        }

        let machine = this.cloneMachine();
        machine.dialog = this._dialog.updateText(text);

        return machine;
    };
}
